import { Game } from "../game";
import { t } from "../utils/translations/phrases";
import { InputManager } from "./inputManager";
import { TrenchHud } from "./trenchHud";

/**
 * "Field manual": a card with the controls, shown in the middle of the screen at the start of a match
 * until the player closes it once. Closed, it folds into a small Controls chip under the players
 * counter (top right); clicking the chip opens it again. Keys come from the player's own bindings.
 */
// v2: the Gravity Gloves row was added (everyone sees the card once more); v3: the inventory row; v4: the gloves'
// plasma burst; v5 (0.93): the voice reactions on X, cancelling healing on H; v6 (1.06): jumping out of the plane and
// steering the chute (and the card waits until you're down on the ground)
const SEEN_KEY = "trenches_controls_seen_v6";

/** the keys the card names in words (in the language picked: asked as the card is filled) */
const prettyKeys = (): Record<string, string> => ({
    Mouse0: t("Left click"), Mouse1: t("Middle click"), Mouse2: t("Right click"),
    MWheelUp: t("Wheel up"), MWheelDown: t("Wheel down"),
    ArrowUp: "\u2191", ArrowDown: "\u2193", ArrowLeft: "\u2190", ArrowRight: "\u2192",
    Space: t("Space"), Escape: t("Esc")
});
const pretty = (key: string): string => prettyKeys()[key] ?? (key.length === 1 ? key.toUpperCase() : key);
const keysFor = (action: string, max = 2): string[] => InputManager.binds.getInputsBoundToAction(action).filter(key => key !== "").slice(0, max).map(pretty);
const escapeHtml = (text: string): string => text.replace(/[&<>"']/gu, char => `&#${char.charCodeAt(0)};`);
// (a key's name as text: an imported settings file can name a key anything, markup too, and it ran as the card was
// filled; 0.94 audit)
const kbd = (keys: string[]): string => keys.length ? keys.map(key => `<kbd>${escapeHtml(key)}</kbd>`).join("") : `<kbd class="none">${t("not set")}</kbd>`;

class ControlsCardClass {
    private _card?: HTMLDivElement;
    private _chip?: HTMLButtonElement;
    /** the card is to be shown once you're on the ground (a match starts in a plane: it would hide the plane and the jump) */
    private _due = false;

    private _build(): void {
        if (this._card) return;
        const gameUi = document.getElementById("game-ui");
        if (!gameUi) return;
        const card = document.createElement("div");
        card.id = "th-controls-card";
        card.hidden = true;
        const chip = document.createElement("button");
        chip.type = "button";
        chip.id = "th-controls-chip";
        chip.title = t("Show the controls");
        chip.innerHTML = `<i class="fa-solid fa-keyboard"></i> ${t("Controls")}`;
        chip.hidden = true;
        // clicks on the card and chip must not reach the game (no shots fired while reading)
        for (const element of [card, chip]) {
            for (const type of ["pointerdown", "mousedown", "mouseup", "click"]) element.addEventListener(type, event => event.stopPropagation());
            // a clicked button gives the keyboard back to the game (focused, it took every key: the game ignored them all,
            // a key held during the click stayed held, and Space pressed the button again)
            element.addEventListener("click", event => (event.target as HTMLElement | null)?.closest("button")?.blur());
        }
        chip.addEventListener("click", () => this.open());
        gameUi.appendChild(card);
        const counters = document.getElementById("counter-holder");
        if (counters) counters.after(chip);
        else gameUi.appendChild(chip);
        this._card = card;
        this._chip = chip;
    }

    private _fill(): void {
        if (!this._card) return;
        const move = ["+up", "+left", "+down", "+right"].map(action => keysFor(action, 1)[0] ?? "?");
        const rows: Array<[string, string]> = [
            [t("Move"), kbd(move)],
            // (1.06: every match starts in a plane)
            [t("Jump out of the plane \u00B7 steer your chute"), kbd([...keysFor("jump", 1), ...move])],
            [t("Shoot"), kbd(keysFor("+attack", 1))],
            [t("Pick up \u00B7 open doors"), kbd(keysFor("interact"))],
            [t("Reload"), kbd(keysFor("reload"))],
            [t("Weapons"), kbd(["slot 0", "slot 1", "slot 2", "equip_or_cycle_throwables 1"].map(action => keysFor(action, 1)[0] ?? "?"))],
            [t("Last weapon"), kbd(keysFor("last_item"))],
            [t("Field dressing \u00B7 medkit"), kbd([...keysFor("use_consumable gauze", 1), ...keysFor("use_consumable medikit", 1)])],
            [t("Cola \u00B7 field meat"), kbd([...keysFor("use_consumable cola", 1), ...keysFor("use_consumable meat", 1)])],
            [t("Cancel healing"), kbd(keysFor("cancel_action"))],
            [t("Detonate C4"), kbd(keysFor("explode_c4"))],
            [t("Gas can: pour \u00B7 flick a lit match where you aim"), kbd([...keysFor("+attack", 1), ...keysFor("reload", 1)])],
            [t("Build a wooden wall (4 wood, from trees)"), kbd(keysFor("build_wall"))],
            // (1.05: at night everyone carries one)
            [t("Flashlight on / off (at night)"), kbd(keysFor("flashlight"))],
            // the Gravity Gloves: hold attack to grab, the wheel moves it out and in, right-click launches (or with
            // nothing held fires a plasma burst), V merges
            [t("Gravity gloves: grab · push / pull · launch or plasma · merge"), kbd([...keysFor("+attack", 1), t("Wheel"), ...keysFor("gravity_launch", 1), ...keysFor("gravity_merge", 1)])],
            [t("Inventory \u00B7 spares \u00B7 drop"), kbd(keysFor("toggle_inventory"))],
            [t("Map"), kbd(keysFor("toggle_map"))],
            [t("Emotes \u00B7 ping"), kbd([...keysFor("+emote_wheel", 1), ...keysFor("+map_ping_wheel", 1)])],
            [t("Voice reactions (from the Store): hold, point, let go"), kbd(keysFor("+reaction_wheel"))]
        ];
        this._card.innerHTML = `
            <div class="tc-head"><span class="tc-title">${t("FIELD MANUAL")}</span><span class="tc-sub">${t("Controls")}</span></div>
            <div class="tc-grid">${rows.map(([label, keys]) => `<span class="tc-label">${label}</span><span class="tc-keys">${keys}</span>`).join("")}</div>
            <div class="tc-foot"><span>${t("Molotovs and poured fuel burn; smoke and water put fire out. Keys can be changed in Settings.")}</span><button type="button" class="tc-close">${t("Got it")}</button></div>`;
        this._card.querySelector(".tc-close")?.addEventListener("click", () => this.close());
    }

    /** a match started: first time, show the card; afterwards only the chip */
    matchStarted(): void {
        if (!TrenchHud.active) return;
        this._build();
        if (!this._card || !this._chip) return;
        let seen = false;
        try { seen = localStorage.getItem(SEEN_KEY) === "1"; } catch { /* storage blocked */ }
        this._card.hidden = true;
        this._chip.hidden = false;
        this._due = !seen;
    }

    /** every frame: a card that's due opens once you're down on the ground, playing */
    update(): void {
        if (!this._due) return;
        const me = Game.activePlayer;
        if (!me || me.air || me.dead || Game.spectating || Game.delayedView || Game.replaying || Game.gameOver) return;
        this._due = false;
        this.open();
    }

    open(): void {
        if (!this._card || !this._chip) return;
        this._fill();
        this._card.hidden = false;
        this._card.classList.remove("tc-leaving");
        this._chip.hidden = true;
    }

    /**
     * put away, not closed (a death replay, watching on: it stayed in the middle of the screen through them, 1.06.1's
     * audit): not counted as read, so the next match shows it again
     */
    putAway(): void {
        if (!this._card || !this._chip || this._card.hidden) return;
        this._card.hidden = true;
        this._chip.hidden = false;
    }

    close(): void {
        if (!this._card || !this._chip) return;
        try { localStorage.setItem(SEEN_KEY, "1"); } catch { /* storage blocked */ }
        this._card.classList.add("tc-leaving");
        this._chip.hidden = false;
        this._chip.classList.remove("tc-arrive");
        void this._chip.offsetWidth; // restart the arrival animation
        this._chip.classList.add("tc-arrive");
        setTimeout(() => {
            if (this._card?.classList.contains("tc-leaving")) {
                this._card.hidden = true;
                this._card.classList.remove("tc-leaving");
            }
        }, 260);
    }
}

export const ControlsCard = new ControlsCardClass();
